<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { ExpertPlan } from "@shopexperts/legacy-api";
import {
  UiButton,
  UiDatePicker,
  UiForm,
  UiFormField,
  UiInfoBox,
  UiSelect,
  UiText,
} from "@shopexperts/shared";
import { useAdminPlanBillingOptions } from "@/app/composables/expert-billing/useAdminPlanBillingOptions";
import {
  defaultPaymentInvitationFormValues,
  invitationExpiryFormValue,
  invitationOptionSelectLabel,
  isInvitationOptionEligible,
  paymentInvitationFormSchema,
  type PaymentInvitationFormValues,
} from "@/app/composables/expert-billing/paymentInvitation";

const props = defineProps<{
  plans: ExpertPlan[];
  isSubmitting?: boolean;
}>();

const emit = defineEmits<{
  submit: [values: PaymentInvitationFormValues];
  cancel: [];
}>();

const initialFormValues = defaultPaymentInvitationFormValues();
const formModel = ref<PaymentInvitationFormValues>({ ...initialFormValues });
const selectedPlanId = computed(() => {
  const value = Number(formModel.value.v2_plan_id);
  return Number.isInteger(value) && value > 0 ? value : null;
});
const selectedPlan = computed(() => props.plans.find((plan) => plan.id === selectedPlanId.value) ?? null);
const canSubmit = computed(() => (
  selectedPlanId.value !== null && Number(formModel.value.v2_plan_billing_option_id) > 0
));
const { options, isLoading: isLoadingOptions } = useAdminPlanBillingOptions(selectedPlanId);

const planOptions = computed(() => props.plans.map((plan) => ({
  label: plan.name,
  value: String(plan.id),
})));
const eligibleOptions = computed(() => options.value.filter(
  (option: Parameters<typeof isInvitationOptionEligible>[0]) => isInvitationOptionEligible(option),
));
const billingOptionOptions = computed(() => {
  if (!selectedPlan.value) return [];
  return eligibleOptions.value.map((option) => ({
    label: invitationOptionSelectLabel(selectedPlan.value!, option),
    value: String(option.id),
  }));
});

watch(selectedPlanId, (next, previous) => {
  if (next === previous) return;
  formModel.value = { ...formModel.value, v2_plan_billing_option_id: "" };
});

function clearExpiry() {
  formModel.value = { ...formModel.value, expires_at: "" };
}
</script>

<template>
  <UiForm
    v-model="formModel"
    :schema="paymentInvitationFormSchema"
    :initial-values="initialFormValues"
    scroll-to-first-error
    class="flex min-h-0 flex-1 flex-col"
    @submit="emit('submit', $event)"
  >
    <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6 md:px-8">
      <div class="space-y-1">
        <UiText as="h3" variant="h4" class="font-semibold text-se-dark" text="Issue payment invitation" />
        <UiText
          as="p"
          variant="p-small"
          class="text-se-gray-700"
          text="Choose the exact Stripe-backed offer this expert can purchase."
        />
      </div>

      <UiInfoBox variant="info" title="One active invitation per offer">
        <UiText as="p" variant="p-small" class="text-se-gray-700">
          Issuing the same plan and billing option replaces its previous pending invitation. An active Stripe Checkout temporarily locks replacement and cancellation.
        </UiText>
      </UiInfoBox>

      <UiInfoBox v-if="plans.length === 0" variant="warning" title="No eligible plans">
        <UiText as="p" variant="p-small" class="text-se-gray-700">
          This expert needs an active plan for their expert type before an invitation can be issued.
        </UiText>
      </UiInfoBox>

      <UiFormField name="v2_plan_id">
        <template #default="{ modelValue, onUpdateModelValue, onValidate, invalid, error, disabled }">
          <div class="flex flex-col gap-1">
            <UiText as="label" for="payment-invitation-plan" variant="h6" text="Plan" />
            <UiSelect
              id="payment-invitation-plan"
              name="v2_plan_id"
              :options="planOptions"
              placeholder="Choose an eligible plan"
              :model-value="(modelValue as string)"
              :invalid="invalid"
              :error="error"
              :disabled="disabled || plans.length === 0"
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
            />
          </div>
        </template>
      </UiFormField>

      <UiFormField name="v2_plan_billing_option_id">
        <template #default="{ modelValue, onUpdateModelValue, onValidate, invalid, error, disabled }">
          <div class="flex flex-col gap-1">
            <UiText as="label" for="payment-invitation-billing-option" variant="h6" text="Billing option" />
            <UiSelect
              id="payment-invitation-billing-option"
              name="v2_plan_billing_option_id"
              :options="billingOptionOptions"
              :placeholder="isLoadingOptions ? 'Loading Stripe-backed options…' : selectedPlanId ? 'Choose a billing option' : 'Choose a plan first'"
              :model-value="(modelValue as string)"
              :invalid="invalid"
              :error="error"
              :disabled="disabled || !selectedPlanId || isLoadingOptions || billingOptionOptions.length === 0"
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
            />
          </div>
        </template>
      </UiFormField>

      <UiInfoBox
        v-if="selectedPlanId && !isLoadingOptions && billingOptionOptions.length === 0"
        variant="warning"
        title="No Stripe-backed options"
      >
        <UiText as="p" variant="p-small" class="text-se-gray-700">
          Add or activate a billing option with a current Stripe price before issuing this invitation.
        </UiText>
      </UiInfoBox>

      <UiFormField name="expires_at">
        <template #default="{ modelValue, onUpdateModelValue, onValidate, invalid, error, disabled }">
          <div class="flex flex-col gap-1">
            <div class="flex items-center justify-between gap-3">
              <UiText as="label" for="payment-invitation-expiry" variant="h6" text="Custom expiry date" />
              <UiText as="span" variant="caption-normal" class="text-se-gray-700" text="Optional" />
            </div>
            <UiDatePicker
              id="payment-invitation-expiry"
              name="expires_at"
              placeholder="Use the default expiry"
              :model-value="(modelValue as any)"
              :invalid="invalid"
              :error="error"
              :disabled="disabled"
              @update:model-value="(value) => onUpdateModelValue(invitationExpiryFormValue(value))"
              @validate="onValidate"
            />
          </div>
        </template>
      </UiFormField>

      <div class="flex items-center justify-between gap-4">
        <UiText
          as="p"
          variant="caption-normal"
          class="text-se-gray-600"
          text="Leave blank to use the server default, normally 7 days."
        />
        <UiButton
          v-if="formModel.expires_at"
          variant="outline"
          size="sm"
          label="Use default"
          @click="clearExpiry"
        />
      </div>
    </div>

    <div class="flex flex-col-reverse gap-3 border-t border-se-gray-200 px-6 py-5 sm:flex-row sm:justify-end md:px-8">
      <UiButton variant="outline" label="Cancel" :disabled="isSubmitting" @click="emit('cancel')" />
      <UiButton
        type="submit"
        variant="primary"
        icon="arrow-send"
        :label="isSubmitting ? 'Issuing…' : 'Issue invitation'"
        :disabled="isSubmitting || !canSubmit"
      />
    </div>
  </UiForm>
</template>
